import { useState, useRef, useCallback, useEffect } from "react";
import { getSyncStatus, CompletedStep, SyncType } from "@/services/sync.quickbooks.service";

const ENTITY_DISPLAY_NAMES: Record<string, string> = {
  vendors: "vendors",
  customers: "customers",
  paymentMethods: "payment methods",
  glAccounts: "chart of accounts",
  bills: "bills",
  expenses: "expenses",
  vendorPayments: "vendor payments",
  invoices: "invoices",
  customerPayments: "customer payments",
  bankAccounts: "bank accounts",
  bankTransactions: "bank transactions",
};

const TOTAL_SYNC_ENTITIES = Object.keys(ENTITY_DISPLAY_NAMES).length;

export function formatStepMessage(step: CompletedStep): string {
  const displayName = ENTITY_DISPLAY_NAMES[step.entity] || step.entity;
  if (step.error) return `${displayName} sync failed`;
  const parts: string[] = [];
  if (step.created > 0) parts.push(`${step.created} ${displayName} added`);
  if (step.updated > 0) parts.push(`${step.updated} ${displayName} updated`);
  if (parts.length === 0) return `${displayName} synced — no changes`;
  return parts.join(", ");
}

interface UseSyncPollingResult {
  currentStep: string;
  completedSteps: CompletedStep[];
  displayedProgress: number;
  isComplete: boolean;
  error: string | null;
  startPolling: (targetSyncId?: string) => Promise<void>;
  stopPolling: () => void;
}

export function useSyncPolling(initialStep = "Syncing data...", syncType?: SyncType): UseSyncPollingResult {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<CompletedStep[]>([]);
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const targetProgress = Math.min(
    100,
    Math.round((completedSteps.length / TOTAL_SYNC_ENTITIES) * 100)
  );

  useEffect(() => {
    if (isComplete) {
      setDisplayedProgress(100);
      return;
    }
    if (displayedProgress >= targetProgress) return;
    const timer = setTimeout(() => {
      setDisplayedProgress((prev) => Math.min(prev + 1, targetProgress));
    }, 30);
    return () => clearTimeout(timer);
  }, [displayedProgress, targetProgress, isComplete]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startPolling = useCallback(async (targetSyncId?: string) => {
    return new Promise<void>((resolve, reject) => {
      let trackedSyncId: string | null = targetSyncId ?? null;

      pollingRef.current = setInterval(async () => {
        try {
          const status = await getSyncStatus();
          if (!status) {
            console.debug("[sync-polling] getSyncStatus returned null");
            return;
          }

          if (!trackedSyncId) {
            const candidates = [status.currentSync, status.lastSync].filter(Boolean);
            const source = syncType
              ? candidates.find(s => s!.syncType === syncType) ?? null
              : (candidates[0] ?? null);
            if (source) {
              trackedSyncId = source.id;
              console.debug("[sync-polling] tracking sync id:", trackedSyncId, "type:", source.syncType);
            } else {
              console.debug("[sync-polling] no matching sync found yet");
              return;
            }
          }

          const sync =
            (status.currentSync?.id === trackedSyncId
              ? status.currentSync
              : null) ||
            (status.lastSync?.id === trackedSyncId
              ? status.lastSync
              : null);

          if (!sync) return;

          if (sync.currentStep) {
            setCurrentStep(sync.currentStep + "...");
          }
          if (sync.completedSteps && sync.completedSteps.length > 0) {
            setCompletedSteps(sync.completedSteps);
          }
          if (sync.state === "COMPLETED" || sync.state === "COMPLETED_WITH_ERRORS") {
            stopPolling();
            setIsComplete(true);
            resolve();
          } else if (sync.state === "FAILED") {
            stopPolling();
            const msg =
              "errorMessage" in sync
                ? (sync as { errorMessage?: string }).errorMessage || "Sync failed"
                : "Sync failed";
            setError(msg);
            reject(new Error(msg));
          }
        } catch {
          // Polling errors are non-fatal, just skip this tick
        }
      }, 1500);
    });
  }, [stopPolling, syncType]);

  return {
    currentStep,
    completedSteps,
    displayedProgress,
    isComplete,
    error,
    startPolling,
    stopPolling,
  };
}
